import React, { useContext, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { Box, Popper, TextField, useMediaQuery, ClickAwayListener } from "@mui/material";
import { LocalizationProvider, DatePicker, PickersDay, DateCalendar } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import AppContext from "../../../../config/AppContext";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import utcPlugin from "dayjs/plugin/utc";
import { styled } from "@mui/styles";

dayjs.extend(isoWeek);
dayjs.extend(isBetweenPlugin);
dayjs.extend(utcPlugin);



const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: (prop) => prop !== "isStartDate" && prop !== "isEndDate" && prop !== "isInRange",
})(({ isStartDate, isEndDate, isInRange, day, mode }) => ({
    borderRadius: 0,
    padding: '20px !important',

    ...(isInRange && {
        color: mode === "dark" ? "#FFFFFF" : "#242424",
        background: mode === "dark" ? "#362C20" : "#FFF7ED",
        "&:hover, &:focus": {
            background: mode === "dark" ? "#362C20" : "#FFF7ED",
            opacity: 0.9,
        },
    }),

    ...(isStartDate && {
        background: "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
        color: mode === "dark" ? "#FFFFFF" : "#242424",
        borderColor: "#388E3C",
        borderRadius: "5px",
        paddingBottom: "18px !important",
    }),

    ...(isEndDate && {
        background: "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
        color: mode === "dark" ? "#FFFFFF" : "#fff",
        borderColor: "#388E3C",
        borderRadius: "5px",
        paddingBottom: "18px !important",
    }),

    ...(day.isSame(dayjs(), "day") && {
        borderRadius: "5px",
        borderWidth: "1px !important",
        borderColor: "#FA9C2B !important",
        color: mode === "dark" ? "#FFFFFF" : "#242424",
        background: mode === "dark" ? "#362C20" : "#FFF7ED",
        paddingBottom: "18px !important",
    }),

    "&.Mui-disabled": {
        color: mode === "dark" ? "#666" : "#ccc",
    },
}));

const StyledDateCalendar = styled(DateCalendar)(({ mode }) => ({
    backgroundColor: mode === "light" ? "#ffffff" : "#1f1f1f",
    color: mode === "light" ? "#242424" : "#f8f8f8",
    position: "absolute",
    zIndex: 1000,
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",

    "& .MuiPickersCalendarHeader-root": {
        color: mode === "light" ? "#242424" : "#f8f8f8",
    },

    "& .MuiPickersCalendarHeader-switchViewButton, & .MuiPickersArrowSwitcher-button": {
        color: mode === "light" ? "#242424" : "#f8f8f8",
        "&:hover": {
            backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
        },
        "&:disabled": {
            color: mode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
        },
    },

    "& .MuiDayCalendar-weekDayLabel": {
        color: mode === "light" ? "#242424" : "#f8f8f8",
    },

    "& .MuiTypography-root": {
        color: mode === "light" ? "#242424" : "#f8f8f8",
    },

    "& .MuiPickersDay-root.Mui-disabled": {
        color: mode === "dark" ? "#666" : "#ccc",
    },

    "@media (max-width: 600px)": {
        padding: "10px",
    },
}));

const isInRange = (day, range) => {
    if (!range?.start || !range?.end) return false;
    return day.isAfter(range.start.subtract(1, 'day')) && day.isBefore(range.end.add(1, 'day'));
};

function Day(props) {
    const { day, ...other } = props;
    const theme = useTheme();
    const { mode } = useContext(AppContext);
    const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));

    const dayStyles = {
        px: isXlScreen ? 2.5 : 2.5,
    };

    const isStartDate = day.isSame(props.weekRange.start, "day");
    const isEndDate = day.isSame(props.weekRange.end, "day");

    return (
        <CustomPickersDay
            {...other}
            day={day}
            mode={mode}
            sx={dayStyles}
            disableMargin
            isInRange={isInRange(day, props.weekRange)}
            isStartDate={isStartDate}
            isEndDate={isEndDate}
        />
    );
}


const CustomizedButtonWeekPicker = forwardRef(({ onClosePop = () => { }, ...props }, ref) => {
    const { mode } = useContext(AppContext);
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(props.error);
    const [selectedDate, setSelectedDate] = useState(
        props.value ? dayjs(props.value) : null
    );
    const initialWeekRef = useRef({ start: null, end: null });

    const [weekRange, setWeekRange] = useState({ start: null, end: null });
    const [anchorEl, setAnchorEl] = useState(null);
    const didSelectWeekRef = useRef(false);

    React.useEffect(() => {
        dayjs.locale(i18n.language || "en");
    }, [i18n.language]);

    React.useEffect(() => {
        setWeekRange({ start: null, end: null });
    }, [props.reset]);


    const theme = createTheme({
        components: {
            MuiPickersPopper: {
                styleOverrides: {
                    root: {
                        marginTop: "10px !important",
                    },
                },
            },
            MuiDateCalendar: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === "light" ? "#ffffff" : "#1f1f1f",
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                },
            },
            MuiPickersCalendarHeader: {
                styleOverrides: {
                    root: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    label: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    switchViewButton: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                },
            },
            MuiDayCalendar: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === "light" ? "#ffffff" : "#1f1f1f",
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    weekDayLabel: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    weekContainer: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                        "&.Mui-selected": {
                            color: "#fff",
                            borderRadius: "5px",
                            background:
                                "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box !important",
                            borderColor: "#FA9C2B",
                        },
                        "&.MuiPickersDay-today": {
                            borderRadius: "5px",
                            border: "1px solid #fa9c2b !important",
                            color: mode === "light" ? "#242424" : "#f8f8f8",
                            background: mode === "dark" ? "#362C20" : "#FFF7ED",
                            "&:hover": {
                                color: "#fff",
                                borderRadius: "5px",
                                background:
                                    "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
                                borderColor: "#FA9C2B",
                            },
                        },
                        ":hover": {
                            color: "#fff",
                            borderRadius: "5px",
                            background:
                                "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
                            borderColor: "#FA9C2B",
                        },
                        "&.Mui-disabled": {
                            color:
                                mode === "light"
                                    ? "#24242480 !important"
                                    : "#f8f8f880 !important",
                        },

                    },
                },
            },
            MuiPickersArrowSwitcher: {
                styleOverrides: {
                    button: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                        "&:hover": {
                            backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
                        },
                        "&:disabled": {
                            color: mode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
                        },
                    },
                },
            },
        },
    });

    useImperativeHandle(ref, () => ({
        openPicker: (buttonElement) => {
            setAnchorEl(buttonElement);
            setWeekRange({ start: null, end: null });
            setOpen(true);
            setSelectedDate(null);
            initialWeekRef.current = weekRange;
            didSelectWeekRef.current = false;
            if (props.onOpenChange) props.onOpenChange(true);
        },
        closePicker: () => {
            setOpen(false);
            if (props.onOpenChange) props.onOpenChange(false);
        }
    }));

    useEffect(() => {
        setError(props.error);
    }, [props.error]);

    const handleDateChange = (newDate) => {
        if (!newDate) return;

        didSelectWeekRef.current = true;

        const startOfWeek = newDate.startOf('day');
        const endOfWeek = startOfWeek.add(6, 'day');

        setSelectedDate(startOfWeek);
        setWeekRange({ start: startOfWeek, end: endOfWeek });
        if (props.onChange) {
            props.onChange({
                startDate: startOfWeek.toDate(),
                endDate: endOfWeek.toDate(),
            });
        }
        setOpen(false);
    };

    const handleClose = () => {
        if (!didSelectWeekRef.current) {
            setWeekRange(initialWeekRef.current); // Restore old selection
            if (props.onChange && initialWeekRef.current.start && initialWeekRef.current.end) {
                props.onChange({
                    startDate: initialWeekRef.current.start.toDate(),
                    endDate: initialWeekRef.current.end.toDate(),
                });
            }
        }
        didSelectWeekRef.current = false;
        setOpen(false);
        onClosePop();
    };

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    open={open}
                    onClose={handleClose}
                     value={selectedDate}
                    disableFuture={true}
                    onChange={handleDateChange}
                    minDate={dayjs('2025-01-01')}
                    showDaysOutsideCurrentMonth
                    displayWeekNumber={false}
                    slots={{
                        textField: () => null,
                        day: Day
                    }}
                    slotProps={{
                        popper: {
                            anchorEl: anchorEl,
                            placement: "bottom-start",
                            modifiers: [
                                {
                                    name: 'preventOverflow',
                                    enabled: true,
                                    options: {
                                        altBoundary: true,
                                        boundary: 'viewport',
                                        padding: 10,
                                    },
                                },
                                {
                                    name: 'flip',
                                    enabled: true,
                                    options: {
                                        fallbackPlacements: ['top-start', 'bottom-start'],
                                    },
                                },
                            ]
                        },
                        day: (ownerState) => ({
                            weekRange: weekRange,
                        }),
                    }}
                />
            </LocalizationProvider>
        </ThemeProvider>
    );
});

export default CustomizedButtonWeekPicker;
